import { Controller } from "@hotwired/stimulus";

// Right drawer + dimmed backdrop for "Manage facility access" on manage-invites producer detail.
export default class extends Controller {
  static targets = [
    "backdrop",
    "panel",
    "form",
    "collaborationId",
    "productLabel",
    "startYear",
    "endYear",
    "audit",
    "voluntary",
    "facilityName",
    "facilityExternalId",
    "facilityFlagIcon",
    "countryName",
    "producerEmailLink",
    "confirmDialog",
    "confirmModalEmailLink",
    "submitButton",
  ];

  connect() {
    this.onKeydown = this.onKeydown.bind(this);
    this.confirmBypass = false;
    this.initialYearsSorted = [];
    this.initialAudit = false;
    this.initialVoluntary = false;
    this.initialProductLabel = "";
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown);
    document.body.classList.remove("overflow-hidden");
  }

  open(event) {
    event.preventDefault();
    const el = event.currentTarget;
    const id = el.dataset.mfaCollaborationId;
    if (this.hasCollaborationIdTarget) {
      this.collaborationIdTarget.value = id || "";
    }
    // Prefer getAttribute so prefilled labels are reliably read (dataset + HTML escaping edge cases).
    this.productLabelTarget.value =
      el.getAttribute("data-mfa-product-label") ?? el.dataset.mfaProductLabel ?? "";
    this.startYearTarget.value = el.dataset.mfaStartYear || "";
    this.endYearTarget.value = el.dataset.mfaEndYear || "";
    this.auditTarget.checked = el.dataset.mfaAudit === "true";
    this.voluntaryTarget.checked = el.dataset.mfaVoluntary === "true";
    this.facilityNameTarget.textContent = el.dataset.mfaFacilityName || "";
    this.facilityExternalIdTarget.textContent =
      el.dataset.mfaFacilityExternalId || "—";
    this.countryNameTarget.textContent = el.dataset.mfaCountryName || "";

    const code = (el.dataset.mfaCountryCode || "").toLowerCase();
    if (this.hasFacilityFlagIconTarget) {
      this.facilityFlagIconTarget.className = code ? `fi fi-${code}` : "fi";
      this.facilityFlagIconTarget.style.fontSize = "20px";
    }

    const email = el.dataset.mfaProducerEmail || "";
    if (this.hasProducerEmailLinkTarget) {
      this.producerEmailLinkTarget.href = email ? `mailto:${email}` : "#";
      this.producerEmailLinkTarget.textContent = email || "—";
    }
    if (this.hasConfirmModalEmailLinkTarget) {
      this.confirmModalEmailLinkTarget.href = email ? `mailto:${email}` : "#";
      this.confirmModalEmailLinkTarget.textContent = email || "—";
    }

    const yearsRaw = el.dataset.mfaYearsSorted || "";
    this.initialYearsSorted = yearsRaw
      .split(",")
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    this.initialAudit = el.dataset.mfaAudit === "true";
    this.initialVoluntary = el.dataset.mfaVoluntary === "true";
    this.initialProductLabel = (
      el.getAttribute("data-mfa-product-label") ??
      el.dataset.mfaProductLabel ??
      ""
    ).trim();

    this.syncSubmitButtonState();

    this.backdropTarget.classList.remove("hidden");
    this.panelTarget.classList.remove("translate-x-full");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.onKeydown);
  }

  close() {
    if (this.hasConfirmDialogTarget && this.confirmDialogTarget.open) {
      this.confirmDialogTarget.close();
    }
    this.backdropTarget.classList.add("hidden");
    this.panelTarget.classList.add("translate-x-full");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close();
  }

  backdropClick(event) {
    if (event.target === this.backdropTarget) this.close();
  }

  /** True if product label or any collaboration term differs from values when the drawer was opened. */
  hasFormChanges() {
    const label = (this.productLabelTarget.value || "").trim();
    if (label !== (this.initialProductLabel || "").trim()) return true;
    return this.termsWouldChange();
  }

  syncSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = !this.hasFormChanges();
  }

  /** Mirrors ManageInvitesController#update_collaboration `terms_changed` (years, audit, voluntary). */
  termsWouldChange() {
    if (this.auditTarget.checked !== this.initialAudit) return true;
    if (this.voluntaryTarget.checked !== this.initialVoluntary) return true;

    const sy = parseInt(this.startYearTarget.value, 10) || 0;
    const ey = parseInt(this.endYearTarget.value, 10) || 0;
    let newYearsSorted;
    if (sy > 0 && ey > 0) {
      const lo = Math.min(sy, ey);
      const hi = Math.max(sy, ey);
      newYearsSorted = [];
      for (let y = lo; y <= hi; y++) newYearsSorted.push(y);
    } else {
      newYearsSorted = [...this.initialYearsSorted];
    }

    if (newYearsSorted.length !== this.initialYearsSorted.length) return true;
    for (let i = 0; i < newYearsSorted.length; i++) {
      if (newYearsSorted[i] !== this.initialYearsSorted[i]) return true;
    }
    return false;
  }

  handleSubmit(event) {
    if (this.confirmBypass) {
      this.confirmBypass = false;
      return;
    }
    if (!this.hasFormChanges()) {
      event.preventDefault();
      return;
    }
    if (!this.termsWouldChange()) return;
    event.preventDefault();
    if (this.hasConfirmDialogTarget) this.confirmDialogTarget.showModal();
  }

  closeConfirmDialog() {
    if (this.hasConfirmDialogTarget) this.confirmDialogTarget.close();
  }

  confirmTermsSubmit() {
    this.closeConfirmDialog();
    this.confirmBypass = true;
    this.formTarget.requestSubmit();
  }
}
